import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { 
  ArrowLeft, Trophy, Target, Lock, Sparkles, 
  Award, TrendingUp, Users, Brain
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import AppFooter from '../components/AppFooter';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;

const AchievementsPage = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useAuth();
  const [achievements, setAchievements] = useState({ unlocked: [], locked: [] });
  const [loading, setLoading] = useState(true);
  const [selectedTier, setSelectedTier] = useState('all'); // all, bronze, silver, gold

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/auth');
      return;
    }
    fetchAchievements();
  }, [isAuthenticated]);

  const fetchAchievements = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/achievements`, {
        withCredentials: true
      });
      setAchievements(response.data);
    } catch (error) {
      console.error('Error fetching achievements:', error);
    } finally {
      setLoading(false);
    }
  };

  const getTierColor = (tier) => {
    switch (tier) {
      case 'gold':
        return 'from-yellow-400 to-orange-500';
      case 'silver':
        return 'from-slate-300 to-slate-400';
      case 'bronze':
        return 'from-orange-300 to-orange-500';
      default:
        return 'from-slate-400 to-slate-500';
    }
  };

  const getTierBadgeClass = (tier) => {
    switch (tier) {
      case 'gold':
        return 'bg-gradient-to-r from-yellow-400 to-orange-500 text-white';
      case 'silver':
        return 'bg-gradient-to-r from-slate-400 to-slate-500 text-white';
      case 'bronze':
        return 'bg-gradient-to-r from-orange-400 to-orange-600 text-white';
      default:
        return 'bg-slate-300 text-slate-700';
    }
  };

  const filterAchievements = (achievementsList) => {
    if (selectedTier === 'all') return achievementsList;
    return achievementsList.filter(a => a.tier === selectedTier);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const renderAchievementCard = (achievement, isUnlocked) => (
    <Card 
      key={achievement.id} 
      className={`relative overflow-hidden transition-all hover:shadow-lg ${
        isUnlocked ? 'border-2 border-purple-200' : 'opacity-60'
      }`}
    >
      {/* Tier Badge */}
      <div className="absolute top-3 right-3">
        <Badge className={getTierBadgeClass(achievement.tier)}>
          {achievement.tier.toUpperCase()}
        </Badge>
      </div>

      <CardHeader className="pb-4">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`w-16 h-16 rounded-full bg-gradient-to-br ${getTierColor(achievement.tier)} flex items-center justify-center text-3xl ${!isUnlocked && 'opacity-50'}`}>
            {isUnlocked ? <Trophy className="w-8 h-8 text-white" /> : <Lock className="w-8 h-8 text-white" />}
          </div>

          {/* Title and Description */}
          <div className="flex-1">
            <CardTitle className={`text-lg mb-2 ${!isUnlocked && 'text-slate-500'}`}>
              {achievement.achievement_name}
            </CardTitle>
            <p className={`text-sm ${isUnlocked ? 'text-slate-700' : 'text-slate-500'}`}>
              {achievement.achievement_description}
            </p>
          </div>
        </div>
      </CardHeader>

      <CardContent>
        {/* Progress Bar */}
        <div className="mb-3">
          <div className="flex justify-between text-xs text-slate-600 mb-1">
            <span>Progress</span>
            <span className="font-semibold">{achievement.progress} / {achievement.target}</span>
          </div>
          <div className="w-full bg-slate-200 rounded-full h-2.5 overflow-hidden">
            <div 
              className={`h-full bg-gradient-to-r ${getTierColor(achievement.tier)} transition-all duration-500 ${isUnlocked && 'animate-pulse'}`}
              style={{ width: `${Math.min(100, (achievement.progress / achievement.target) * 100)}%` }}
            />
          </div>
        </div>

        {/* Unlock Date */}
        {isUnlocked && achievement.unlocked_at && (
          <div className="flex items-center gap-2 text-xs text-purple-600 mt-3">
            <Sparkles className="w-3 h-3" />
            <span>Unlocked on {formatDate(achievement.unlocked_at)}</span>
          </div>
        )}

        {/* Remaining Progress */}
        {!isUnlocked && achievement.progress < achievement.target && (
          <div className="text-xs text-slate-500 mt-3">
            {achievement.target - achievement.progress} more to unlock
          </div>
        )}
      </CardContent>
    </Card>
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50 flex items-center justify-center">
        <div className="text-center">
          <Brain className="w-12 h-12 text-purple-600 animate-spin mx-auto mb-4" />
          <p className="text-slate-600">Loading achievements...</p>
        </div>
      </div>
    );
  }

  const totalAchievements = achievements.unlocked.length + achievements.locked.length;
  const completionPercentage = totalAchievements > 0 
    ? Math.round((achievements.unlocked.length / totalAchievements) * 100)
    : 0;

  return (
    <div className="min-h-screen bg-gradient-to-br from-purple-50 via-pink-50 to-orange-50">
      {/* Header */}
      <header className="border-b bg-white/80 backdrop-blur-sm sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Button
              variant="ghost"
              className="gap-2"
              onClick={() => navigate('/dashboard')}
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Dashboard
            </Button>
            <div className="text-center">
              <h1 className="text-2xl font-bold bg-gradient-to-r from-yellow-600 via-orange-600 to-red-600 bg-clip-text text-transparent">
                My Achievements
              </h1>
              <p className="text-sm text-slate-600">Track your progress and unlock rewards</p>
            </div>
            <div className="w-32"></div>
          </div>
        </div>
      </header>

      <div className="container mx-auto px-6 py-8 max-w-7xl">
        {/* Stats Overview */}
        <div className="grid md:grid-cols-4 gap-6 mb-8">
          <Card className="border-2 border-purple-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full flex items-center justify-center">
                  <Trophy className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-purple-600">{achievements.total_unlocked}</p>
                  <p className="text-sm text-slate-600">Unlocked</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-blue-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-full flex items-center justify-center">
                  <Target className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-blue-600">{completionPercentage}%</p>
                  <p className="text-sm text-slate-600">Completion</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-yellow-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-full flex items-center justify-center">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-yellow-600">{totalAchievements}</p>
                  <p className="text-sm text-slate-600">Total</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="border-2 border-green-200">
            <CardContent className="pt-6">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-emerald-500 rounded-full flex items-center justify-center">
                  <TrendingUp className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="text-2xl font-bold text-green-600">{achievements.locked.length}</p>
                  <p className="text-sm text-slate-600">In Progress</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-3 mb-6">
          {['all', 'gold', 'silver', 'bronze'].map((tier) => (
            <Button
              key={tier}
              variant={selectedTier === tier ? 'default' : 'outline'}
              onClick={() => setSelectedTier(tier)}
              className={selectedTier === tier ? 'bg-gradient-to-r from-purple-600 to-pink-600' : ''}
            >
              {tier === 'all' ? 'All' : tier.charAt(0).toUpperCase() + tier.slice(1)}
            </Button>
          ))}
        </div>

        {/* Unlocked Achievements */}
        {filterAchievements(achievements.unlocked).length > 0 && (
          <div className="mb-12">
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Sparkles className="w-6 h-6 text-yellow-500" />
              Unlocked Achievements
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {filterAchievements(achievements.unlocked).map((achievement) => 
                renderAchievementCard(achievement, true)
              )}
            </div>
          </div>
        )}

        {/* Locked Achievements */}
        {filterAchievements(achievements.locked).length > 0 && (
          <div>
            <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-2">
              <Lock className="w-6 h-6 text-slate-500" />
              Locked Achievements
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {filterAchievements(achievements.locked).map((achievement) => 
                renderAchievementCard(achievement, false)
              )}
            </div>
          </div>
        )}

        {/* No achievements message */}
        {totalAchievements === 0 && (
          <Card className="text-center py-12">
            <CardContent>
              <Trophy className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-bold text-slate-700 mb-2">No Achievements Yet</h3>
              <p className="text-slate-500 mb-6">
                Start using Idea Fountain to unlock achievements!
              </p>
              <Button 
                onClick={() => navigate('/session/new')}
                className="bg-gradient-to-r from-purple-600 to-pink-600"
              >
                Start Your First Session
              </Button>
            </CardContent>
          </Card>
        )}
      </div>

      <AppFooter />
    </div>
  );
};

export default AchievementsPage;
